import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { Settings, Link as LinkIcon, UserPlus } from 'lucide-react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import {
  Button, SegmentedToggle, Skeleton, Dialog, DialogContent, DialogHeader, DialogTitle,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, MultiPick, Badge,
} from '@duck/ui'
import { toast } from 'sonner'
import { useTar } from '@/modules/i18n'
import { usePartnerTar, PartnerOrgTypeContext } from '@/hooks/usePartnerTar'
import { useAuth } from '@/hooks/useAuth'
import { useUpdateCohort } from '@/hooks/useCohorts'
import { useCohortBoard } from '@/hooks/useCohortBoard'
import { useOrganization } from '@/hooks/useOrganization'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { usePeopleDirectory } from '@/hooks/usePeopleDirectory'
import { useOrganizationSettings, useOrganizationAdminSettings } from '@/hooks/useOrganizationSettings'
import { CreateCohortDialog } from '../CreateCohortDialog'
import { DeleteDialog } from '@/components/DeleteDialog/DeleteDialog'
import { AddCohortPersonDialog } from '../People/AddCohortPersonDialog'
import { SendAssignment } from '../Assignments/SendAssignment'
import { AssignmentsTab } from './assignments/AssignmentsTab'
import { PeopleTab } from './people/PeopleTab'
import { ErrorState } from '@/components/EmptyStates'
import { CohortDialogsCard } from './CohortDialogsCard'
import { cohortPutPayload } from '../Dialogs/cohortPutPayload'

interface CohortPageProps {
  cohortId: string
}

type Tab = 'assignments' | 'people'

/**
 * Resolves the VIEWED org's type for partner copy before rendering the page: admins
 * have no organizationType of their own, so without the provider a school cohort
 * renders company vocabulary (candidates/positions instead of students/classes).
 * The board query is cached, so the content component's own useCohortBoard call
 * shares the same request.
 */
export const CohortPage: React.FC<CohortPageProps> = ({ cohortId }) => {
  const { isAdmin } = useAuth()
  const { cohort } = useCohortBoard(cohortId)
  const { organization } = useOrganization(isAdmin ? cohort?.organizationId : undefined)
  return (
    <PartnerOrgTypeContext.Provider value={organization?.type}>
      <CohortPageContent cohortId={cohortId} />
    </PartnerOrgTypeContext.Provider>
  )
}

const CohortPageContent: React.FC<CohortPageProps> = ({ cohortId }) => {
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const { organizationId, isAdmin } = useAuth()
  // The board endpoint returns the cohort, its assignments (incl. empty public links),
  // submission details, and a taskId→name map in ONE request — no per-assignment / per-task fan-out.
  const { cohort, assignments, assignmentsDetails, taskNames, isLoading, isError, error, refetch } = useCohortBoard(cohortId)
  useDocumentTitle(cohort?.name ?? cohortId, 'partner')
  // The people directory is fetched once (cached, shared) and joined client-side. It resolves
  // person identity (name/email/joinedAt) and does NOT block board rendering — names fill in
  // progressively as it loads. Partners get their own org implicitly; an admin scopes it to the
  // cohort's org (resolved from the board) so they don't pull every person in the system.
  const directoryOrganizationId = isAdmin ? cohort?.organizationId : undefined
  const { directoryById, isLoading: directoryLoading, refetch: refetchDirectory } =
    usePeopleDirectory(directoryOrganizationId)
  const { getOrgSetting } = useOrganizationSettings()
  const { getOrgAdminSetting } = useOrganizationAdminSettings()
  const updateCohort = useUpdateCohort(cohortId, organizationId ?? undefined)

  // Resolve a task display name from the board's taskNames map (replaces the per-task useTaskDisplayNames).
  const getTaskDisplayName = useCallback((taskId: string) => taskNames[taskId] ?? taskId, [taskNames])

  // The Assignments tab resolves roster/joiner names via persons.find(p => p.id === personId).
  // Directory entries (id/firstName/lastName/email/userId) are a drop-in lookup source and cover
  // public-link joiners (who are NOT in cohort.personIds) as well as members.
  const directoryPersons = useMemo(() => Array.from(directoryById.values()), [directoryById])

  // Challenge selector (Assignments tab): multi-select; an empty selection means "all challenges".
  // Drop any selected ids that no longer exist after a refetch.
  const availableTaskIds = useMemo(
    () => Array.from(new Set(assignments.map((a) => a.taskId))),
    [assignments]
  )
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([])
  useEffect(() => {
    setSelectedTaskIds((prev) => prev.filter((id) => availableTaskIds.includes(id)))
  }, [availableTaskIds])

  // The People tab's expanded roster labels each line with its challenge name; resolve assignmentId
  // → taskId (from the board's assignments) → display name (from the board's taskNames map).
  const taskIdByAssignment = useMemo(() => {
    const map = new Map<string, string>()
    for (const a of assignments) map.set(a.id, a.taskId)
    return map
  }, [assignments])
  const getAssignmentTaskName = useCallback(
    (assignmentId: string) => {
      const taskId = taskIdByAssignment.get(assignmentId)
      return taskId ? getTaskDisplayName(taskId) : ''
    },
    [taskIdByAssignment, getTaskDisplayName]
  )

  // The People tab shows ONLY manually-added cohort members (not public-link joiners), per the
  // product definition of "candidates".
  const memberPersonIds = useMemo(() => cohort?.personIds ?? [], [cohort?.personIds])

  // Header summary counts (each shown only when > 0).
  const publicCount = useMemo(() => assignments.filter((a) => a.linkToken).length, [assignments])
  const sentCount = assignments.length - publicCount
  const peopleCount = cohort?.personIds?.length ?? 0

  const allowPublicLinks = getOrgAdminSetting('allowPublicLinks') ?? false

  const [tab, setTab] = useState<Tab>('assignments')
  const [checkedPersonIds, setCheckedPersonIds] = useState<Set<string>>(new Set())
  const [addOpen, setAddOpen] = useState(false)
  const [sendOpen, setSendOpen] = useState(false)
  const [publicOpen, setPublicOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [closeOpen, setCloseOpen] = useState(false)

  // Board data drives the page; the directory only fills identity in progressively, so it does
  // not gate the header or the assignment/people lists.
  const loading = isLoading

  const handleTabChange = (next: Tab) => {
    if (next === 'assignments') setCheckedPersonIds(new Set())
    setTab(next)
  }

  const goSend = () => {
    if (checkedPersonIds.size === 0) {
      setTab('people')
      toast.error(tP('pleaseSelectPeople'))
      return
    }
    setSendOpen(true)
  }

  if (isError || (!isLoading && !cohort)) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-foreground">{tP('cohort')}</h1>
        {error ? (
          <ErrorState
            title={t('common.errorLoading', { item: tP('cohort').toLowerCase() })}
            description={t('common.errorLoadingDescription')}
            onRetry={() => refetch()}
          />
        ) : (
          <ErrorState
            icon={MagnifyingGlassIcon}
            title={t('common.notFound', { item: tP('cohort') })}
            description={t('common.notFoundDescription')}
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {loading || !cohort ? (
            <><Skeleton className="h-9 w-1/3" /><Skeleton className="mt-2 h-4 w-1/2" /></>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-foreground">
                {cohort.name}
                {!cohort.isActive && <span className="text-muted-foreground"> ({t('partner.closed')})</span>}
              </h1>
              {(() => {
                const parts: string[] = []
                if (sentCount > 0) parts.push(t('partner.assignmentsCount', { count: sentCount }))
                if (publicCount > 0) parts.push(t('partner.publicAssignmentsCount', { count: publicCount }))
                if (peopleCount > 0) parts.push(tP('assignedPeopleCount', { count: peopleCount }))
                return parts.length > 0 ? (
                  <p className="mt-1 text-sm text-muted-foreground">{parts.join(' · ')}</p>
                ) : null
              })()}
              {cohort.description && <p className="mt-2 text-sm text-muted-foreground">{cohort.description}</p>}
            </>
          )}
        </div>
        {!loading && cohort && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={tP('cohortSettings')}><Settings className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditOpen(true)}>{tP('editDetails')}</DropdownMenuItem>
              {cohort.isActive ? (
                <DropdownMenuItem onClick={() => setCloseOpen(true)}>{tP('closeCohort')}</DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={async () => {
                  try {
                    await updateCohort.mutateAsync(cohortPutPayload(cohort, { isActive: true }))
                    toast.success(tP('cohortReopened'))
                  } catch { toast.error(t('errors.somethingWentWrongWeveBeenNotified')) }
                }}>{tP('reopenCohort')}</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {!loading && cohort && (
        <CohortDialogsCard
          cohort={cohort}
          onSave={async (patch) => {
            await updateCohort.mutateAsync(cohortPutPayload(cohort, patch))
            await refetch()
          }}
        />
      )}

      {/* Tabs + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedToggle<Tab>
          value={tab}
          onChange={handleTabChange}
          segments={[
            { value: 'assignments', label: t('partner.assignments') },
            { value: 'people', label: tP('people') },
          ]}
        />
        <div className="flex items-center gap-2">
          {tab === 'assignments' ? (
            allowPublicLinks && (
              <Button className="gap-2" disabled={!cohort?.isActive} title={!cohort?.isActive ? tP('cohortClosedActionsDisabled') : undefined} onClick={() => setPublicOpen(true)}>
                <LinkIcon className="h-4 w-4" />{t('partner.createPublicAssignment')}
              </Button>
            )
          ) : (
            <Button className="gap-2" disabled={!cohort?.isActive} title={!cohort?.isActive ? tP('cohortClosedActionsDisabled') : undefined} onClick={() => setAddOpen(true)}>
              <UserPlus className="h-4 w-4" />{tP('addPeople')}
            </Button>
          )}
        </div>
      </div>

      {/* Challenge selector (Assignments tab) — its own row. Empty selection = all challenges. */}
      {tab === 'assignments' && availableTaskIds.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">{t('challenges.challenge')}:</span>
          {availableTaskIds.length === 1 ? (
            <Badge variant="outline">{getTaskDisplayName(availableTaskIds[0])}</Badge>
          ) : (
            <MultiPick
              className="w-80"
              searchable={false}
              options={availableTaskIds.map((id) => ({ label: getTaskDisplayName(id), value: id }))}
              selected={selectedTaskIds}
              onChange={setSelectedTaskIds}
              labels={{ placeholder: t('common.all') }}
            />
          )}
        </div>
      )}

      {/* Body */}
      {tab === 'assignments' ? (
        <AssignmentsTab
          assignments={assignments}
          assignmentsDetails={assignmentsDetails}
          persons={directoryPersons}
          isLoading={loading}
          selectedTaskIds={selectedTaskIds}
          onAssignmentChange={refetch}
          allowPublicLinks={allowPublicLinks}
          onAddPeople={() => { handleTabChange('people'); setAddOpen(true) }}
          onCreatePublicAssignment={() => setPublicOpen(true)}
        />
      ) : (
        <PeopleTab
          cohortId={cohortId}
          memberPersonIds={memberPersonIds}
          assignmentsDetails={assignmentsDetails}
          directoryById={directoryById}
          directoryLoading={directoryLoading}
          getAssignmentTaskName={getAssignmentTaskName}
          isLoading={loading}
          checkedPersonIds={checkedPersonIds}
          setCheckedPersonIds={setCheckedPersonIds}
          onSendToSelected={goSend}
        />
      )}

      {/* Dialogs */}
      <AddCohortPersonDialog
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        cohortId={cohortId}
        cohort={cohort}
        parentManagesCohort
        onPersonAdded={(createdPersonIds) => {
          setTab('people')
          setCheckedPersonIds(new Set(createdPersonIds)) // auto-select → selection bar nudges Send
          refetch()
          // New members' identity (name/email) lives in the directory, not the board; refetch it so
          // their rows resolve instead of showing the raw personId until the 30-min staleTime.
          refetchDirectory()
        }}
        onAssignmentSent={() => {
          setTab('assignments')
          refetch()
          // Sending an assignment invites the people, flipping their onboardingStatus Added→Invited.
          // That status lives in the directory (not the board), so refetch it or the just-invited
          // rows stay "Added" — hiding the Copy Link CTA — until the 30-min staleTime expires.
          refetchDirectory()
        }}
      />

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent width="sm">
          <DialogHeader><DialogTitle>{t('partner.sendAssignment')}</DialogTitle></DialogHeader>
          <SendAssignment
            cohortId={cohortId}
            personIds={Array.from(checkedPersonIds)}
            publicMode={false}
            parentManagesCohort
            sendInvitationEmails={getOrgSetting('invitationEmails') ?? false}
            onClose={() => setSendOpen(false)}
            onSend={() => { setSendOpen(false); setCheckedPersonIds(new Set()); setTab('assignments'); refetch(); refetchDirectory() }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={publicOpen} onOpenChange={setPublicOpen}>
        <DialogContent width="sm">
          <DialogHeader><DialogTitle>{t('partner.createPublicAssignment')}</DialogTitle></DialogHeader>
          <SendAssignment
            cohortId={cohortId}
            publicMode
            parentManagesCohort
            onClose={() => setPublicOpen(false)}
            onSend={() => { setPublicOpen(false); setTab('assignments'); refetch() }}
          />
        </DialogContent>
      </Dialog>

      <CreateCohortDialog isOpen={editOpen} onClose={() => setEditOpen(false)} cohort={cohort || null} onCohortUpdated={refetch} />

      <DeleteDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title={tP('closeCohort')}
        description={tP('closeCohortDescription')}
        deleteButtonText={tP('closeCohort')}
        onDelete={async () => {
          if (!cohort) throw new Error('Cohort is missing')
          await updateCohort.mutateAsync(cohortPutPayload(cohort, { isActive: false }))
        }}
      />
    </div>
  )
}
